import React from 'react';
import { useLocation, useNavigate, matchPath } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { Box, ButtonBase, Typography } from '@mui/material';

// Modern, elegant MUI Icons
import ExploreIcon from '@mui/icons-material/Explore';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import PeopleIcon from '@mui/icons-material/People';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import AddIcon from '@mui/icons-material/Add';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import LoginIcon from '@mui/icons-material/Login';

import { RED, RED_HOVER } from '../../utils/brandColors';

const BOTTOM_NAV_HEIGHT = 56;

const Root = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(18, 20, 22, 0.85)' 
    : 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  // No borderTop: the hard 1px line read as a seam against the feed. The
  // blur + soft lift shadow below carry the separation instead.
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-around',
  zIndex: theme.zIndex.appBar,
  paddingTop: 6,
  paddingBottom: 'calc(6px + env(safe-area-inset-bottom, 0px))',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 -8px 28px rgba(0, 0, 0, 0.34)'
    : '0 -8px 28px rgba(0, 0, 0, 0.07)',
  // Hide on desktop
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
}));

const NavButton = styled(ButtonBase)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 3,
  height: '100%',
  transition: 'color 0.15s ease',
  // text.secondary, not disabled: the 0.65rem labels measured 3.9:1 in dark
  // with the disabled tone - below AA for text this small.
  color: theme.palette.text.secondary,
  position: 'relative',
  paddingTop: 4,
  paddingBottom: 4,
}));

const CreateButton = styled(ButtonBase)(({ theme }) => ({
  width: 44,
  height: 44,
  borderRadius: 14,
  backgroundColor: RED,
  boxShadow: theme.shadows[2],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',
  color: '#fff',
  '&:active': {
    transform: 'scale(0.92)',
  },
  '&:hover': {
    backgroundColor: RED_HOVER,
    boxShadow: theme.shadows[4],
  }
}));

// Build navigation configuration
const buildTabs = (userInfo) => {
  let lastTab;
  if (userInfo && !userInfo.isAnon) {
    lastTab = {
      title: 'Profile',
      path: '/u/me',
      IconActive: PersonIcon,
      IconInactive: PersonOutlinedIcon
    };
  } else {
    lastTab = {
      title: 'Sign in',
      path: '/login',
      IconActive: LoginIcon,
      IconInactive: LoginIcon
    };
  }
  // PARITY: the desktop sidebar is `display:none` below the `lg` breakpoint,
  // so a nav row added only to NavConfig would be desktop-only. The social hub
  // has to be reachable on mobile too, and the bottom bar is capped at five
  // tabs. For signed-in users the second slot therefore becomes People (which
  // links onward to Discover); anonymous users keep Discover, since they have
  // no social graph to manage and People is gated behind AuthGate anyway.
  const isLoggedIn = userInfo && !userInfo.isAnon;
  const secondTab = isLoggedIn
    ? { title: 'People', path: '/people', IconActive: PeopleIcon, IconInactive: PeopleOutlinedIcon, badgeKey: 'followRequests' }
    : { title: 'Discover', path: '/discover', IconActive: PeopleIcon, IconInactive: PeopleOutlinedIcon };

  return [
    { title: 'Explore', path: '/', IconActive: ExploreIcon, IconInactive: ExploreOutlinedIcon },
    secondTab,
    { title: 'Create', path: '/new', IconActive: AddIcon, IconInactive: AddIcon, isCreate: true },
    { title: 'Challenge', path: '/play', IconActive: EmojiEventsIcon, IconInactive: EmojiEventsOutlinedIcon },
    lastTab,
  ];
};

export { BOTTOM_NAV_HEIGHT };

export default function BottomNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.userLogin);
  const followRequests = useSelector((s) => s.social?.pendingRequests || 0);
  // Unread DM conversations. Combined with follow requests on the People tab
  // because People is the phone gateway to Messages (MessagesButton is hidden
  // below `sm`, the sidebar below `lg`) - without this, phone users have no
  // unread signal in the chrome at all. Pending message requests are already
  // excluded from this count server-side, so a stranger can't light the badge.
  const dmUnread = useSelector((s) => s.social?.dmUnread?.conversations || 0);
  const tabs = buildTabs(userInfo);
  const peopleBadge = followRequests + dmUnread;

  const isActive = (path) => {
    if (path === '/') {
      return pathname === '/';
    }
    return !!matchPath({ path, end: false }, pathname);
  };

  // Hide on fullscreen and active gameplay experiences
  const hideOnPaths = [
    '/v/',
    '/play/rapid',
    '/play/contrarian',
    '/play/streak',
    '/play/blindbet',
    '/play/daily',
    '/new',
  ];
  if (hideOnPaths.some((p) => pathname.startsWith(p))) return null;
  if (/^\/play\/category\/.+/.test(pathname)) return null;
  if (/^\/play\/versus\/.+/.test(pathname)) return null;
  if (/^\/play\/agree\/.+/.test(pathname)) return null;
  if (/^\/play\/mindmeld\/.+/.test(pathname)) return null;
  if (/^\/play\/memory\/.+/.test(pathname)) return null;

  return (
    <Root>
      {tabs.map((tab) => {
        const active = isActive(tab.path);

        if (tab.isCreate) {
          return (
            <Box key={tab.title} sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <CreateButton onClick={() => navigate(tab.path)} aria-label="Create">
                <AddIcon sx={{ fontSize: 24 }} />
              </CreateButton>
            </Box>
          );
        }

        const IconComponent = active ? tab.IconActive : tab.IconInactive;

        return (
          <NavButton key={tab.title} onClick={() => navigate(tab.path)} aria-label={tab.title}>
            {active && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  width: 16,
                  height: 3,
                  borderRadius: '0 0 3px 3px',
                  backgroundColor: RED,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                  animation: 'activeBarEntrance 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  '@keyframes activeBarEntrance': {
                    from: { transform: 'scaleX(0)' },
                    to: { transform: 'scaleX(1)' },
                  },
                }}
              />
            )}
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <IconComponent
                sx={{
                  fontSize: 22,
                  color: active ? RED : 'inherit',
                  transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transform: active ? 'scale(1.15) translateY(-2px)' : 'scale(1)',
                }}
              />
              {tab.badgeKey === 'followRequests' && peopleBadge > 0 && (
                <Box
                  data-testid="bottomnav-badge-followRequests"
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -8,
                    minWidth: 16,
                    height: 16,
                    px: 0.5,
                    borderRadius: '50px',
                    bgcolor: RED,
                    color: '#fff',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {peopleBadge > 9 ? '9+' : peopleBadge}
                </Box>
              )}
            </Box>
            <Typography
              sx={{
                fontSize: '0.65rem',
                fontWeight: active ? 800 : 500,
                color: active ? (theme) => theme.palette.text.primary : 'inherit',
                lineHeight: 1,
                transition: 'all 0.2s ease',
              }}
            >
              {tab.title}
            </Typography>
          </NavButton>
        );
      })}
    </Root>
  );
}
